import React, { useState } from "react";
import { Shield, FlaskConical, Cpu, Wand2, Sparkles, AlertTriangle, CheckCircle2, XCircle, Dna, Play, Square, Bot, Layers } from "lucide-react";
import { generateSgSummary, applySgToLive, sgBotStart, sgBotStop, sgBotMode, sgBotNetwork } from "@/lib/smartgrid_api";
import { ConfirmLiveDialog, ConfirmPaperDialog } from "@/components/ConfirmDialogs";
import { toast } from "sonner";

const C = {
    panel: "#0F1620", border: "#1B2A3A", text: "#E6EDF6", sub: "#A7B4C6",
    dim: "#6F7D92", accent: "#2EE6D6", bull: "#2BE38B", bear: "#FF5C6C", warn: "#FFB020", info: "#3DA7FF",
};

const PanelHead = ({ icon: Icon, title, right }) => (
    <div className="px-4 py-3 border-b flex items-center gap-2" style={{ borderColor: C.border }}>
        <Icon size={14} color={C.accent} />
        <span className="text-[11px] uppercase tracking-[0.16em]" style={{ color: C.sub }}>{title}</span>
        {right && <span className="ml-auto">{right}</span>}
    </div>
);

export const Sparkline = ({ data = [], color = C.accent, w = 90, h = 24 }) => {
    if (!data || data.length < 2) return <svg width={w} height={h} />;
    const min = Math.min(...data), max = Math.max(...data);
    const span = max - min || 1;
    const pts = data.map((v, i) => `${(i / (data.length - 1)) * w},${h - ((v - min) / span) * h}`).join(" ");
    const up = data[data.length - 1] >= data[0];
    return (
        <svg width={w} height={h} style={{ display: "block" }}>
            <polyline points={pts} fill="none" stroke={up ? C.bull : C.bear} strokeWidth="1.5" />
        </svg>
    );
};

const Toggle = ({ on, onClick, labelOn, labelOff, colorOn = "#2BE38B", testId, disabled }) => (
    <button onClick={disabled ? undefined : onClick} disabled={disabled}
        title={disabled ? "Stop the bot to change this" : undefined}
        className="btn-anim px-3 py-1 rounded-md text-[11px] font-semibold"
        style={{ background: on ? colorOn : "#13202D", color: on ? "#0B0F14" : "#A7B4C6",
            border: `1px solid ${on ? colorOn : "#1B2A3A"}`, opacity: disabled ? 0.5 : 1,
            cursor: disabled ? "not-allowed" : "pointer" }}
        data-testid={testId}>
        {on ? labelOn : labelOff}
    </button>
);

export const BotControlPanel = ({ status, onChanged }) => {
    const s = status || {};
    const running = !!s.running;
    const isLive = s.mode === "live";
    const isTestnet = s.use_testnet !== false;
    const [busy, setBusy] = useState(false);
    const [liveDlg, setLiveDlg] = useState(null);   // null | {kind:"mode"|"start"}
    const [paperDlg, setPaperDlg] = useState(false); // live -> paper confirm

    const act = async (fn) => {
        setBusy(true);
        try { await fn(); onChanged && onChanged(); } catch (_) { /* ignore */ } finally { setBusy(false); }
    };

    const onToggleMode = () => {
        if (isLive) { setPaperDlg(true); return; }   // leaving LIVE needs confirm
        setLiveDlg({ kind: "mode" });   // entering LIVE needs OTP
    };
    const onPaperConfirmed = () => {
        setPaperDlg(false);
        act(() => sgBotMode("paper"));
    };

    const onStart = async () => {
        if (!isLive) return act(() => sgBotStart(s.mode || "paper"));
        // already in LIVE mode — try (rides recent OTP grant), else prompt OTP
        setBusy(true);
        try { await sgBotStart("live"); onChanged && onChanged(); }
        catch (e) {
            if (e?.response?.status === 403) setLiveDlg({ kind: "start" });
            else toast.error(e?.response?.data?.detail || "Start failed");
        } finally { setBusy(false); }
    };

    const onLiveVerified = async () => {
        const kind = liveDlg?.kind;
        setLiveDlg(null);
        try {
            if (kind === "mode") { await sgBotMode("live"); toast.success("SmartGrid switched to LIVE"); }
            else { await sgBotStart("live"); toast.success("SmartGrid started in LIVE"); }
            onChanged && onChanged();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "LIVE action failed");
        }
    };

    return (
        <div className="panel" data-testid="sg-bot-control">
            <PanelHead icon={Bot} title="SmartGrid Autonomous Bot (isolated engine · sgb-)"
                right={<span className="text-[10px] px-2 py-0.5 rounded font-mono"
                    style={{ color: running ? C.bull : C.dim, background: "#13202D" }}>
                    {running ? "TRADING" : "STOPPED"}
                </span>} />
            <div className="p-4 flex flex-wrap items-center gap-x-6 gap-y-3">
                <div className="flex items-center gap-2">
                    {!running ? (
                        <button onClick={onStart} disabled={busy}
                            className="btn-anim inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[12px] font-semibold"
                            style={{ background: C.bull, color: "#0B0F14" }} data-testid="sg-bot-start">
                            <Play size={14} /> Start
                        </button>
                    ) : (
                        <button onClick={() => act(sgBotStop)} disabled={busy}
                            className="btn-anim inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-[12px] font-semibold"
                            style={{ background: C.bear, color: "#0B0F14" }} data-testid="sg-bot-stop">
                            <Square size={14} /> Stop
                        </button>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-[0.12em]" style={{ color: C.dim }}>Mode</span>
                    <Toggle on={isLive} onClick={onToggleMode}
                        labelOn="LIVE" labelOff="PAPER" colorOn={C.bear} testId="sg-bot-mode-toggle" disabled={running} />
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-[0.12em]" style={{ color: C.dim }}>Network</span>
                    <Toggle on={!isTestnet} onClick={() => act(() => sgBotNetwork(isTestnet ? false : true))}
                        labelOn="MAINNET" labelOff="TESTNET" colorOn={C.warn} testId="sg-bot-network-toggle" disabled={running} />
                </div>
                <div className="flex-1" />
                <div className="flex items-center gap-6">
                    <div><div className="text-[10px] uppercase" style={{ color: C.dim }}>Active BUYs</div>
                        <div className="font-mono text-sm" style={{ color: C.text }}>{s.active_buys ?? 0}</div></div>
                    <div><div className="text-[10px] uppercase" style={{ color: C.dim }}>Open SELLs</div>
                        <div className="font-mono text-sm" style={{ color: C.text }}>{s.active_sells ?? 0}</div></div>
                    <div><div className="text-[10px] uppercase" style={{ color: C.dim }}>Realized</div>
                        <div className="font-mono text-sm" style={{ color: (s.realized_pnl ?? 0) >= 0 ? C.bull : C.bear }}>
                            ${Number(s.realized_pnl ?? 0).toFixed(2)}</div></div>
                    <div><div className="text-[10px] uppercase" style={{ color: C.dim }}>Unrealized</div>
                        <div className="font-mono text-sm" style={{ color: (s.unrealized_pnl ?? 0) >= 0 ? C.bull : C.bear }}>
                            ${Number(s.unrealized_pnl ?? 0).toFixed(2)}</div></div>
                </div>
            </div>
            {isLive && (
                <div className="px-4 pb-3 text-[11px]" style={{ color: C.warn }} data-testid="sg-bot-live-warning">
                    ⚠ LIVE mode trades real funds on a shared wallet — manage balances manually.
                </div>
            )}
            <ConfirmLiveDialog
                open={!!liveDlg}
                onOpenChange={(o) => { if (!o) setLiveDlg(null); }}
                onConfirm={onLiveVerified}
                credsPresent={s.creds_present !== false}
                useTestnet={isTestnet}
                bot="smartgrid"
            />
            <ConfirmPaperDialog
                open={paperDlg}
                onOpenChange={setPaperDlg}
                onConfirm={onPaperConfirmed}
                isRunning={running && isLive}
            />
        </div>
    );
};

export const RegimeLeaderboardPanel = ({ regimeLeaderboard }) => {
    const items = (regimeLeaderboard && regimeLeaderboard.items) || {};
    const learning = (regimeLeaderboard && regimeLeaderboard.learning) || {};
    const minCycles = (regimeLeaderboard && regimeLeaderboard.min_cycles) || 5;
    const entries = Object.entries(items);
    // Regimes still accumulating evidence (no qualified champion yet).
    const progress = Object.entries(learning).filter(([regime]) => !items[regime]);
    return (
        <div className="panel" data-testid="sg-regime-leaderboard">
            <PanelHead icon={Layers} title="Regime-Conditioned Best Params"
                right={<span className="text-[10px] px-2 py-0.5 rounded font-mono"
                    style={{ color: "#6F7D92", background: "#13202D" }}
                    title="Twin-simulated winners. grid/max_buys auto-apply at full autonomy; TP× applies via the exit policy in aggressive posture; SL is never applied (profit-only exits).">
                    sim-learned
                </span>} />
            <div className="p-3">
                {entries.length === 0 && progress.length === 0 &&
                    <div className="text-[11px]" style={{ color: "#3A4759" }}>Learning per-regime winners… (no twin cycles attributed yet)</div>}
                <div className="space-y-1.5">
                    {entries.map(([regime, v]) => (
                        <div key={regime} className="flex items-center gap-2 text-xs" data-testid="sg-regime-best-row">
                            <span className="font-semibold w-28 shrink-0" style={{ color: C.accent }}>{regime}</span>
                            <span className="font-mono" style={{ color: C.sub }}>
                                ${v.params?.grid_size}/{v.params?.tp_mult}x/{v.params?.max_buys}
                            </span>
                            <span className="ml-auto font-mono" style={{ color: (v.metrics?.net_pnl ?? 0) >= 0 ? C.bull : C.bear }}>
                                ${Number(v.metrics?.net_pnl ?? 0).toFixed(2)}
                            </span>
                        </div>
                    ))}
                    {progress.map(([regime, p]) => {
                        const cand = p.candidate || {};
                        const done = Math.min(cand.cycles ?? 0, p.needed ?? minCycles);
                        return (
                            <div key={regime} className="flex items-center gap-2 text-xs" style={{ opacity: 0.6 }} data-testid="sg-regime-learning-row">
                                <span className="font-semibold w-28 shrink-0" style={{ color: C.sub }}>{regime}</span>
                                <span className="font-mono" style={{ color: C.dim }}>
                                    {cand.params ? `$${cand.params.grid_size}/${cand.params.tp_mult}x/${cand.params.max_buys}` : "—"}
                                </span>
                                <span className="ml-auto font-mono" style={{ color: C.dim }}>
                                    learning {done}/{p.needed ?? minCycles}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

const Row = ({ label, value, color = C.text, "data-testid": dtid }) => (
    <div className="flex items-center justify-between py-1.5 border-b" style={{ borderColor: "#13202D" }} data-testid={dtid}>
        <span className="text-[11px] uppercase tracking-[0.1em]" style={{ color: C.dim }}>{label}</span>
        <span className="font-mono text-sm" style={{ color }}>{value}</span>
    </div>
);

export const RiskPanel = ({ risk }) => {
    const r = risk || {};
    const breakers = r.circuit_breakers || [];
    // Advisory notes (e.g. risk-of-ruin) — informational, never actuate the breaker latch.
    const advisories = r.advisories || [];
    return (
        <div className="panel" data-testid="sg-risk-panel">
            <PanelHead icon={Shield} title="Risk Intelligence"
                right={<span className="text-[10px] px-2 py-0.5 rounded font-mono"
                    style={{ color: r.breaker_engaged ? C.bear : C.bull, background: "#13202D" }}>
                    score {r.risk_score ?? "—"}
                </span>} />
            <div className="p-4">
                <Row label="Exposure" value={`$${Number(r.exposure_usdt ?? 0).toFixed(2)}`} />
                <Row label="Held BTC" value={Number(r.held_btc ?? 0).toFixed(5)} />
                <Row label="Kelly Fraction" value={Number(r.kelly_fraction ?? 0).toFixed(3)} color={C.accent} />
                <Row label="Half-Kelly (size)" value={Number(r.half_kelly ?? 0).toFixed(3)} />
                <Row label="VaR 1h (95%)" value={`${Number(r.var_1h_95_pct ?? 0).toFixed(2)}% · $${Number(r.var_1h_95_usdt ?? 0).toFixed(0)}`} color={C.warn} />
                <Row label="Expected Drawdown" value={`${Number(r.expected_drawdown_pct ?? 0).toFixed(1)}%`} color={C.bear} />
                <Row label="Risk of Ruin" value={r.risk_of_ruin_pct == null ? "—" : `${Number(r.risk_of_ruin_pct).toFixed(1)}%`} color={C.bear} />
                <div className="mt-3 space-y-1.5" data-testid="sg-circuit-breakers">
                    {breakers.length === 0 && <div className="text-[11px]" style={{ color: C.bull }}>✓ No circuit breakers engaged</div>}
                    {breakers.map((b) => (
                        <div key={b} className="flex items-start gap-1.5 text-[11px]" style={{ color: C.warn }}>
                            <AlertTriangle size={12} className="mt-0.5 shrink-0" /> {b}
                        </div>
                    ))}
                    {advisories.map((a) => (
                        <div key={a} className="flex items-start gap-1.5 text-[11px]" style={{ color: C.sub }}>
                            <AlertTriangle size={12} className="mt-0.5 shrink-0" /> {a} (advisory)
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export const HypothesisPanel = ({ hypotheses }) => {
    const h = hypotheses || {};
    const active = h.active || [];
    const bayes = h.bayes || {};
    return (
        <div className="panel" data-testid="sg-hypothesis-panel">
            <PanelHead icon={FlaskConical} title="Hypothesis Engine (Bayesian)" />
            <div className="p-3">
                <div className="text-[10px] uppercase tracking-[0.12em] mb-1.5" style={{ color: C.dim }}>Active hypotheses</div>
                <div className="space-y-1.5 mb-3">
                    {active.length === 0 && <div className="text-[11px]" style={{ color: "#3A4759" }}>Generating…</div>}
                    {active.slice(0, 5).map((a) => (
                        <div key={a.id} className="flex items-center gap-2 text-xs" data-testid="sg-hypothesis-row">
                            <span className="font-mono font-semibold" style={{ color: C.accent }}>{a.type}</span>
                            <span style={{ color: C.dim }}>conf {a.confidence}%</span>
                            <span className="ml-auto font-mono" style={{ color: C.sub }}>WR {(a.bayes_win_rate * 100).toFixed(0)}%</span>
                        </div>
                    ))}
                </div>
                <div className="text-[10px] uppercase tracking-[0.12em] mb-1.5" style={{ color: C.dim }}>Learned reliability (win-rate per type)</div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                    {Object.entries(bayes).map(([t, v]) => (
                        <div key={t} className="flex items-center justify-between text-[11px]">
                            <span style={{ color: C.sub }}>{t}</span>
                            <span className="font-mono" style={{ color: v.win_rate >= 0.5 ? C.bull : C.bear }}>
                                {(v.win_rate * 100).toFixed(0)}% <span style={{ color: C.dim }}>({v.samples})</span>
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export const OnlineMlPanel = ({ onlineMl, bandit, meta, generation }) => {
    const o = onlineMl || {};
    const b = bandit || {};
    const m = meta || {};
    const conf = m.confident || {};
    const ev = o.ev ?? 0;
    const fi = o.feature_importance || {};
    const topFeats = Object.entries(fi).filter(([, v]) => v > 0).slice(0, 4);
    // Defensive clamp: a 15m expected return lives well inside ±50%. Guards the
    // UI against any stale/absurd model output slipping through (see #5).
    const expRet = Math.max(-50, Math.min(50, Number(o.expected_return_pct ?? 0)));
    return (
        <div className="panel" data-testid="sg-onlineml-panel">
            <PanelHead icon={Cpu} title="Online ML · Expected Value · Bandit" />
            <div className="p-4">
                <Row label="Expected Value / decision" value={`${ev >= 0 ? "+" : ""}${(ev * 100).toFixed(3)}%`}
                     color={ev >= 0 ? C.bull : C.bear} data-testid="sg-ml-ev" />
                <Row label="P(win) · P(loss)" value={`${((o.p_win ?? 0.5) * 100).toFixed(0)}% · ${((o.p_loss ?? 0.5) * 100).toFixed(0)}%`} />
                <Row label="Expected return (15m)" value={`${expRet >= 0 ? "+" : ""}${expRet.toFixed(3)}%`}
                     color={expRet >= 0 ? C.bull : C.bear} />
                <Row label="P(up)" value={`${((o.p_up ?? 0.5) * 100).toFixed(1)}%`} color={(o.p_up ?? 0.5) >= 0.5 ? C.bull : C.bear} />
                <div className="mt-3" data-testid="sg-ml-feature-importance">
                    <div className="text-[10px] uppercase tracking-[0.12em] mb-1.5" style={{ color: C.dim }}>Top decision drivers</div>
                    {topFeats.length > 0 ? topFeats.map(([k, v]) => (
                        <div key={k} className="flex items-center gap-2 mb-1">
                            <span className="text-[11px] w-24 truncate" style={{ color: C.sub }}>{k}</span>
                            <div className="flex-1 h-1.5 rounded-full" style={{ background: C.border }}>
                                <div className="h-1.5 rounded-full" style={{ width: `${Math.min(100, v * 100)}%`, background: C.accent }} />
                            </div>
                            <span className="font-mono text-[10px]" style={{ color: C.dim }}>{(v * 100).toFixed(0)}%</span>
                        </div>
                    )) : (
                        <div className="text-[11px]" style={{ color: C.dim }}>Awaiting samples…</div>
                    )}
                </div>
                <div className="mt-3 flex items-center gap-3 text-[10px]" style={{ color: C.dim }}>
                    <span>acc {o.accuracy == null ? "—" : `${(o.accuracy * 100).toFixed(0)}%`}</span>
                    <span>· {o.samples ?? 0} samples</span>
                    {o.regimes_tracked != null && <span>· {o.regimes_tracked} regime models</span>}
                    <span style={{ color: o.drift_detected ? C.warn : C.dim }}>· {o.drift_detected ? "drift" : "stable"}</span>
                </div>
                <div className="mt-3 flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-[0.12em]" style={{ color: C.dim }}>Bandit arm</span>
                    <span className="font-mono text-sm" style={{ color: C.accent }} data-testid="sg-bandit-arm">${b.chosen_arm ?? "—"}</span>
                    <span className="text-[9px] uppercase px-1.5 py-0.5 rounded" title="The bandit drives grid spacing once confident (fills the new-regime gap)."
                        style={{ color: b.confident ? C.bull : C.dim, background: "#13202D" }} data-testid="sg-bandit-actuating">
                        {b.confident ? "actuating" : "learning"}
                    </span>
                    <span className="ml-auto flex items-center gap-1.5 text-[11px]" style={{ color: C.info }}>
                        <Dna size={12} /> gen {generation ?? 0}
                    </span>
                </div>
                {m.spacing_mult != null && (
                    <div className="mt-2 pt-2 border-t flex items-center flex-wrap gap-x-3 gap-y-1 text-[11px]" style={{ borderColor: "#13202D" }} data-testid="sg-meta-controller">
                        <span className="text-[10px] uppercase tracking-[0.12em]" style={{ color: C.dim }}>Meta-policy</span>
                        <span style={{ color: conf.spacing ? C.accent : C.dim }}>grid ×{m.spacing_mult}{conf.spacing ? "" : "*"}</span>
                        <span style={{ color: conf.throttle ? C.accent : C.dim }}>throttle {Math.round((m.throttle ?? 1) * 100)}%{conf.throttle ? "" : "*"}</span>
                        <span style={{ color: m.pause ? C.bear : C.bull }}>{m.pause ? "pause" : "trade"}</span>
                        <span className="ml-auto text-[9px]" style={{ color: C.dim }}>* learning</span>
                    </div>
                )}
            </div>
        </div>
    );
};

const Gate = ({ ok, label }) => (
    <div className="flex items-center gap-1.5 text-[11px]" style={{ color: ok ? C.bull : C.dim }}>
        {ok ? <CheckCircle2 size={12} /> : <XCircle size={12} />} {label}
    </div>
);

export const AutoTunePanel = ({ autotune, onApplied, autonomy }) => {
    const a = autotune || {};
    const gates = a.gates || {};
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState("");
    // At FULL autonomy the slow loop auto-applies the same params
    // (ADAPT_PARAMS) — the manual promotion button is redundant there.
    const isFullAutonomy = autonomy === "full";

    const apply = async () => {
        if (!a.to_params) return;
        setBusy(true); setMsg("");
        try {
            const res = await applySgToLive({
                grid_size: a.to_params.grid_size,
                buy_qty: a.to_params.buy_qty,
                sell_qty: a.to_params.buy_qty,
                max_buys: a.to_params.max_buys,
                // tp_mult is applied via the central ExitManager (clamped;
                // active in aggressive posture). trail (the sim's exit style)
                // is intentionally NOT sent: live exits are owned by the
                // ExitManager and profit-only — no loss stop-losses.
                tp_mult: a.to_params.tp_mult,
            });
            setMsg(res.ok ? "Applied to LIVE engine ✓ (TP via exit policy; exits profit-only)"
                          : `Failed: ${res.error || "error"}`);
            onApplied && onApplied(res);
        } catch (e) {
            setMsg("Request failed");
        } finally { setBusy(false); }
    };

    return (
        <div className="panel" data-testid="sg-autotune-panel">
            <PanelHead icon={Wand2} title="Auto-Parameter-Tuning (gated)"
                right={<span className="text-[10px] px-2 py-0.5 rounded font-mono"
                    style={{ color: a.recommend ? C.bull : C.dim, background: "#13202D" }}>
                    {a.recommend ? "PROMOTE" : "HOLD"}
                </span>} />
            <div className="p-4">
                <div className="text-xs mb-3" style={{ color: C.sub }}>{a.reason}</div>
                <div className="grid grid-cols-2 gap-3 mb-3">
                    <div className="rounded-md p-2" style={{ background: "#0B1219", border: `1px solid ${C.border}` }}>
                        <div className="text-[10px] uppercase" style={{ color: C.dim }}>Live-equivalent</div>
                        <div className="font-mono text-[11px]" style={{ color: C.sub }}>
                            {a.from_params ? `$${a.from_params.grid_size}/${a.from_params.tp_mult}x/${a.from_params.max_buys}` : "—"}
                        </div>
                        <div className="font-mono text-[11px]" style={{ color: C.text }}>PnL ${Number(a.from_metrics?.net_pnl ?? 0).toFixed(2)}</div>
                    </div>
                    <div className="rounded-md p-2" style={{ background: "#0B1219", border: `1px solid ${C.accent}40` }}>
                        <div className="text-[10px] uppercase" style={{ color: C.dim }}>Top twin</div>
                        <div className="font-mono text-[11px]" style={{ color: C.accent }}>
                            {a.to_params ? `$${a.to_params.grid_size}/${a.to_params.tp_mult}x/${a.to_params.max_buys}` : "—"}
                        </div>
                        <div className="font-mono text-[11px]" style={{ color: C.bull }}>PnL ${Number(a.to_metrics?.net_pnl ?? 0).toFixed(2)}</div>
                    </div>
                </div>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 mb-3">
                    <Gate ok={gates.enough_cycles} label="Enough cycles" />
                    <Gate ok={gates.positive_expectancy} label="Positive expectancy" />
                    <Gate ok={gates.beats_profit_factor} label="Beats profit factor" />
                    <Gate ok={gates.beats_net_pnl} label="Beats net PnL (+10%)" />
                    <Gate ok={gates.beats_sharpe} label="Beats Sharpe" />
                    <Gate ok={gates.not_already_live} label="Different from live" />
                </div>
                {isFullAutonomy ? (
                    <div className="w-full py-2 rounded-md text-[11px] text-center"
                        style={{ background: "#13202D", color: C.sub }}
                        data-testid="sg-apply-auto-note">
                        Autonomy is FULL — qualified params are auto-applied by the AI
                        (see ADAPT_PARAMS in the action feed). Lower the autonomy ceiling
                        to promote manually.
                    </div>
                ) : (
                    <button
                        onClick={apply}
                        disabled={!a.recommend || busy}
                        className="btn-anim w-full py-2 rounded-md text-[12px] font-semibold"
                        style={{
                            background: a.recommend ? C.accent : "#13202D",
                            color: a.recommend ? "#0B0F14" : C.dim,
                            cursor: a.recommend && !busy ? "pointer" : "not-allowed",
                        }}
                        data-testid="sg-apply-to-live-button"
                    >
                        {busy ? "Applying…" : a.recommend ? "Apply top twin to LIVE engine" : "No promotion (gates not met)"}
                    </button>
                )}
                {msg && <div className="text-[11px] mt-2 text-center" style={{ color: C.sub }} data-testid="sg-apply-msg">{msg}</div>}
            </div>
        </div>
    );
};

export const SummaryPanel = ({ summary, onGenerated }) => {
    const [busy, setBusy] = useState(false);
    const text = summary?.summary;
    const generate = async () => {
        setBusy(true);
        try { const res = await generateSgSummary(); onGenerated && onGenerated(res); }
        catch (e) { /* ignore */ } finally { setBusy(false); }
    };
    return (
        <div className="panel" data-testid="sg-summary-panel">
            <PanelHead icon={Sparkles} title="AI Daily Learning Summary (offline · Claude)"
                right={
                    <button onClick={generate} disabled={busy}
                        className="btn-anim text-[11px] px-2.5 py-1 rounded"
                        style={{ background: "rgba(46,230,214,0.12)", color: C.accent, border: `1px solid ${C.accent}40` }}
                        data-testid="sg-generate-summary-button">
                        {busy ? "Generating…" : "Generate"}
                    </button>
                } />
            <div className="p-4 max-h-[360px] overflow-y-auto">
                {!text && <div className="text-[12px]" style={{ color: "#3A4759" }}>No summary yet — click Generate (uses ~1 LLM call).</div>}
                {text && <pre className="text-[12px] whitespace-pre-wrap font-sans leading-relaxed" style={{ color: C.sub }} data-testid="sg-summary-text">{text}</pre>}
            </div>
        </div>
    );
};
